import type {
  ListPurchasesQuery,
  PaymentSlipDTO,
  Paginated,
  PurchaseDTO,
  RejectPurchaseRequest,
} from '@brainkey/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import { toast } from '../../components/ui/Toast';
import { paymentsApi } from '../api/endpoints';
import { queryKeys } from '../queryClient';

export function usePaymentsList(query: ListPurchasesQuery): UseQueryResult<Paginated<PurchaseDTO>> {
  return useQuery({
    queryKey: queryKeys.payments.list(query as Record<string, unknown>),
    queryFn: ({ signal }) => paymentsApi.list(query, signal),
    placeholderData: (prev) => prev,
  });
}

export function usePaymentSlips(purchaseId: string | null): UseQueryResult<PaymentSlipDTO[]> {
  return useQuery({
    queryKey: queryKeys.payments.slips(purchaseId ?? ''),
    queryFn: ({ signal }) => paymentsApi.listSlips(purchaseId ?? '', signal),
    enabled: purchaseId !== null,
  });
}

function invalidatePayments(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: queryKeys.payments.all });
}

export function useVerifyPayment(): UseMutationResult<PurchaseDTO, unknown, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => paymentsApi.verify(id),
    onSuccess: () => {
      invalidatePayments(qc);
      toast.success('Payment verified');
    },
  });
}

export function useRejectPayment(): UseMutationResult<
  PurchaseDTO,
  unknown,
  { id: string; body: RejectPurchaseRequest }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => paymentsApi.reject(id, body),
    onSuccess: () => {
      invalidatePayments(qc);
      toast.success('Payment rejected');
    },
  });
}
